import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, PenTool, Code, HeadphonesIcon } from 'lucide-react';

const icons = [MessageSquare, PenTool, Code, HeadphonesIcon];

export default function ProcessSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('animate-fade-in-up');
                el.classList.remove('opacity-0');
              }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="process" className="py-12 sm:py-16 lg:py-20 px-5 scroll-mt-16">
      <div className="max-w-lg sm:max-w-3xl lg:max-w-6xl mx-auto">
        <div className="reveal opacity-0 mb-6 lg:mb-10 lg:text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill rounded-full mb-3">
            <span className="w-1 h-1 rounded-full bg-[#00D4FF]" />
            <p className="text-[#00D4FF] text-[10px] font-semibold uppercase tracking-[0.12em]">{t('process.label')}</p>
          </div>
          <h2 className="text-[#F0F2F5] text-2xl sm:text-3xl lg:text-4xl font-bold">{t('process.title')}</h2>
        </div>

        <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-5 lg:grid-cols-4 lg:gap-6">
          {([1, 2, 3, 4] as const).map((stepNum, index) => {
            const Icon = icons[index];
            return (
              <div key={stepNum} className="reveal opacity-0 flex items-start gap-4 md:flex-col md:gap-3 md:items-stretch">
                {/* Number circle */}
                <div className="flex flex-col items-center md:flex-row md:gap-3 md:items-center">
                  <div className="w-12 h-12 rounded-full border-2 border-[rgba(0,212,255,0.3)] flex items-center justify-center flex-shrink-0
                    bg-[rgba(0,212,255,0.06)] text-[#00D4FF] text-lg font-bold">
                    {stepNum}
                  </div>
                  {/* connector — only on mobile vertical stack */}
                  {index < 3 && <div className="w-0.5 h-6 bg-gradient-to-b from-[rgba(0,212,255,0.3)] to-transparent mt-1 md:hidden" />}
                </div>

                {/* Content - glass card */}
                <div className="glass-card flex-1 p-4 lg:p-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon size={16} className="text-[#00D4FF]" />
                    <h3 className="text-[#F0F2F5] text-sm lg:text-[15px] font-semibold">{t(`process.steps.${stepNum}.title`)}</h3>
                  </div>
                  <p className="text-[#8A93A7] text-[11px] lg:text-[12px] leading-relaxed">{t(`process.steps.${stepNum}.desc`)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
